import React, { forwardRef, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { classNames, useSetState, useUpdateRef } from '@zambezi/sdk/common'

/**
 * Create a React Tab Bar component
 *
 * @template T
 * @param {import('./interfaces').TabBarProps<T>} props
 * @returns {JSX.Element}
 */
function tabBar ({
  id,
  className,
  data,
  isTabDisabled,
  withSeparator,
  onSelected,
  selectedIndex: propSelectedIndex,
  renderFn,
  tabPropsFn,
  defaultSelectedIndex,
  manual
}, ref) {
  const wrapperRef = useRef()
  const [{ selectedIndex, focusedIndex }, setState] = useSetState({
    selectedIndex:
      propSelectedIndex !== undefined
        ? propSelectedIndex
        : defaultSelectedIndex,
    focusedIndex: -1
  })
  const wrapperClass = classNames(
    'zb-tab-bar',
    {
      'zb-tab-bar-has-separator': withSeparator
    },
    className
  )
  useUpdateRef(ref, wrapperRef)

  useEffect(() => {
    const newSelectedIndex =
      propSelectedIndex !== undefined ? propSelectedIndex : selectedIndex

    setState({
      selectedIndex: newSelectedIndex,
      focusedIndex: focusedIndex !== -1 ? newSelectedIndex : -1
    })
  }, [propSelectedIndex])

  return (
    <div
      className={wrapperClass}
      id={id}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      ref={wrapperRef}
    >
      <div className='zb-tab-bar-item-container' role='tablist'>{data.map(renderTab)}</div>
    </div>
  )

  function renderTab (d, i) {
    const isDisabled = isTabDisabled(d, i)
    const isSelected = i === selectedIndex
    const isFocused = i === focusedIndex

    return (
      <div
        className={classNames('zb-tab-bar-item', {
          'zb-tab-bar-item-is-selected': isSelected,
          'zb-tab-bar-item-is-disabled': isDisabled,
          'zb-tab-bar-item-is-focused': isFocused
        })}
        {...tabPropsFn(d, i)}
        key={i}
        role='tab'
        aria-selected={isSelected}
        aria-disabled={isDisabled}
        onClick={isDisabled ? null : e => onTabSelected(d, i, e)}
        onFocus={() => {
          onFocus(i)
        }}
        ref={elem => {
          if (elem && isFocused && document.activeElement !== elem) {
            elem.focus()
          }
        }}
        tabIndex={isSelected ? 0 : -1}
      >
        {renderFn(d, i)}
      </div>
    )
  }

  function onFocus (index) {
    setState({ focusedIndex: index })
  }

  function onBlur () {
    setState({ focusedIndex: -1 })
  }

  function onKeyDown (e) {
    const [d, newSelectedIndex] = getNavigatedTab(
      e.key,
      data,
      focusedIndex === -1 ? selectedIndex : focusedIndex
    )

    if (newSelectedIndex !== focusedIndex) {
      onFocus(newSelectedIndex)
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onTabSelected(d, newSelectedIndex, e)
    } else if (selectedIndex !== newSelectedIndex) {
      e.preventDefault()
      if (!manual) {
        onTabSelected(d, newSelectedIndex, e)
      }
    }
  }

  function onTabSelected (d, index, e) {
    if (propSelectedIndex === undefined) {
      setState({
        selectedIndex: index,
        focusedIndex: index
      })
    }

    onSelected(d, index)
  }
}

const TabBar = forwardRef(tabBar)

TabBar.defaultProps = {
  data: [],
  renderFn: String,
  onSelected: () => {},
  isTabDisabled: (d, i) => false,
  selectedIndex: undefined,
  defaultSelectedIndex: 0,
  tabPropsFn: () => ({}),
  manual: false
}

TabBar.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.object, PropTypes.number, PropTypes.string])
  ),
  renderFn: PropTypes.func,
  onSelected: PropTypes.func,
  isTabDisabled: PropTypes.func,
  selectedIndex: PropTypes.number,
  defaultSelectedIndex: PropTypes.number,
  tabPropsFn: PropTypes.func,
  manual: PropTypes.bool
}

export default TabBar

function getNavigatedTab (key, data, selectedIndex) {
  const lastIndex = data.length - 1
  const isLastSelected = selectedIndex === lastIndex
  const isFirstSelected = selectedIndex === 0

  switch (key) {
    case 'ArrowRight':
      return nextNot(
        isLastSelected ? 0 : selectedIndex + 1,
        data
      )
    case 'ArrowLeft':
      return prevNot(
        isFirstSelected ? lastIndex : selectedIndex - 1,
        data
      )
    case 'Home':
      return nextNot(0, data)
    case 'End':
      return prevNot(lastIndex, data)
    default:
      return [data[selectedIndex], selectedIndex]
  }
}

function nextNot (from, data) {
  const max = data.length - 1
  const newIndex = Math.min(from, max)
  const newDatum = data[newIndex]

  return [newDatum, newIndex]
}

function prevNot (from, data) {
  const min = 0
  const newIndex = Math.max(from, min)
  const newDatum = data[newIndex]

  return [newDatum, newIndex]
}
